import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import { PieChart, Pie, Cell, Legend, Tooltip as ReTooltip, ResponsiveContainer } from 'recharts';
import { Clock, PlayCircle, Users, Globe2, Search, TrendingUp, TrendingDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WORLD_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const PIE_COLORS = ['#E9641A', '#8b5cf6', '#22c55e', '#3b82f6', '#eab308', '#ec4899'];
const NAME_ALIASES = {
  'United States': 'United States of America',
  'Dominican Republic': 'Dominican Rep.',
  'Democratic Republic of the Congo': 'Dem. Rep. Congo',
  'Republic of the Congo': 'Congo',
  'Central African Republic': 'Central African Rep.',
  'South Sudan': 'S. Sudan',
  'Ivory Coast': "Côte d'Ivoire",
  'Czech Republic': 'Czechia',
  'Bosnia and Herzegovina': 'Bosnia and Herz.',
};

export const fmtDur = (s) => {
  s = Math.round(s || 0);
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
  if (h) return `${h} h${m ? ` ${m} min` : ''}`;
  if (m) return `${m} min`;
  return s ? `${s} s` : '—';
};
const fdate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
const money = (cents) => `${((cents || 0) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

function WorldMap({ countries, light }) {
  const [geo, setGeo] = useState(null);
  const [hover, setHover] = useState(null);
  useEffect(() => {
    fetch(WORLD_URL).then(r => r.json())
      .then(topo => setGeo(feature(topo, topo.objects.countries).features))
      .catch(() => {});
  }, []);
  const { path, byName, maxSessions } = useMemo(() => {
    const proj = geoNaturalEarth1().fitSize([960, 470], { type: 'Sphere' });
    const byName = {};
    countries.forEach(c => { byName[NAME_ALIASES[c.country] || c.country] = c; });
    return { path: geoPath(proj), byName, maxSessions: Math.max(1, ...countries.map(c => c.sessions)) };
  }, [countries]);
  if (!geo) return <div className="h-[300px] flex items-center justify-center text-xs text-white/30">Chargement de la carte…</div>;
  return (
    <div className="relative" data-testid="world-map">
      <svg viewBox="0 0 960 470" className="w-full h-auto">
        {geo.map((f, i) => {
          const d = byName[f.properties.name];
          const t = d ? Math.sqrt(d.sessions / maxSessions) : 0;
          return (
            <path key={i} d={path(f)}
              fill={d ? `rgba(233,100,26,${0.25 + 0.75 * t})` : (light ? '#e2e8f0' : 'rgba(255,255,255,0.07)')}
              stroke={light ? '#cbd5e1' : 'rgba(255,255,255,0.12)'} strokeWidth={0.5}
              onMouseEnter={() => d && setHover({ ...d })} onMouseLeave={() => setHover(null)}
              style={{ cursor: d ? 'pointer' : 'default', transition: 'fill 0.2s' }} />
          );
        })}
        {geo.map((f, i) => {
          const d = byName[f.properties.name];
          if (!d) return null;
          const [x, y] = path.centroid(f);
          if (!isFinite(x) || !isFinite(y)) return null;
          const r = 9 + 9 * Math.sqrt(d.sessions / maxSessions);
          return (
            <g key={`m${i}`} pointerEvents="none">
              <circle cx={x} cy={y} r={r} fill="#E9641A" opacity={0.9} stroke="#fff" strokeWidth={1.2} />
              <text x={x} y={y + 3.5} textAnchor="middle" fontSize={r > 13 ? 11 : 9} fontWeight={800} fill="#fff">{d.sessions}</text>
            </g>
          );
        })}
      </svg>
      {hover && (
        <div className={`absolute top-2 left-2 px-3 py-2 rounded-lg text-xs shadow-xl border ${light ? 'bg-white border-gray-200 text-gray-800' : 'bg-[#1a0e2e] border-white/10 text-white'}`} data-testid="map-tooltip">
          <p className="font-bold">{hover.country}</p>
          <p className="opacity-70">{hover.sessions} lecture(s) · {hover.users} spectateur(s) · {fmtDur(hover.seconds)}</p>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, light }) {
  return (
    <div className={`rounded-xl border p-4 flex items-center gap-3 ${light ? 'bg-white border-gray-200' : 'bg-white/[0.03] border-white/10'}`}>
      <div className="w-9 h-9 rounded-lg bg-[#E9641A]/10 flex items-center justify-center flex-none"><Icon size={17} className="text-[#E9641A]" /></div>
      <div>
        <p className={`text-lg font-bold leading-tight ${light ? 'text-gray-900' : 'text-white'}`}>{value}</p>
        <p className={`text-[10px] uppercase tracking-wide ${light ? 'text-gray-500' : 'text-white/40'}`}>{label}</p>
      </div>
    </div>
  );
}

export function SubscriberStats({ theme }) {
  const light = theme === 'light';
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [contentOrder, setContentOrder] = useState('top'); // top | flop
  const [contentMetric, setContentMetric] = useState('seconds'); // seconds | views | unique_viewers

  useEffect(() => {
    axios.get(`${API}/admin/subscribers/insights`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => {});
  }, []);

  const contents = useMemo(() => {
    if (!data) return [];
    let list = data.contents_stats;
    if (q) list = list.filter(c => c.title.toLowerCase().includes(q.toLowerCase()));
    list = [...list].sort((a, b) => (b[contentMetric] - a[contentMetric]) || (b.views - a.views));
    if (contentOrder === 'flop') list.reverse();
    return list.slice(0, 30);
  }, [data, q, contentOrder, contentMetric]);

  if (!data) return <div className="py-20 text-center text-xs text-white/40">Chargement des statistiques…</div>;

  const card = light ? 'bg-white border-gray-200' : 'bg-white/[0.03] border-white/10';
  const th = light ? 'text-gray-500' : 'text-white/40';
  const tx = light ? 'text-gray-900' : 'text-white';
  const empty = data.totals.sessions === 0;

  return (
    <div className="space-y-5" data-testid="subscriber-stats">
      {empty && (
        <div className={`rounded-xl border px-4 py-3 text-xs ${light ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-amber-500/10 border-amber-500/30 text-amber-300'}`} data-testid="stats-empty-notice">
          La collecte des données de visionnage (pays, appareils, temps par contenu) vient d'être activée : les statistiques se rempliront au fil des lectures des utilisateurs.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Clock} label="Temps visionné total" value={fmtDur(data.totals.seconds)} light={light} />
        <Kpi icon={PlayCircle} label="Lectures" value={data.totals.sessions} light={light} />
        <Kpi icon={Users} label="Spectateurs" value={data.totals.viewers} light={light} />
        <Kpi icon={Globe2} label="Pays" value={data.totals.countries} light={light} />
      </div>

      {/* World map + countries */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className={`lg:col-span-2 rounded-xl border p-4 ${card}`}>
          <h3 className={`text-sm font-bold mb-2 ${tx}`}>D'où regardent vos utilisateurs</h3>
          <WorldMap countries={data.countries} light={light} />
        </div>
        <div className={`rounded-xl border p-4 ${card}`} data-testid="countries-list">
          <h3 className={`text-sm font-bold mb-3 ${tx}`}>Par pays</h3>
          {data.countries.length === 0 && <p className={`text-xs ${th}`}>Aucune donnée pour le moment</p>}
          <div className="space-y-2 max-h-[330px] overflow-y-auto pr-1">
            {data.countries.map(c => (
              <div key={c.country} className="flex items-center gap-2">
                <span className={`text-xs font-semibold flex-1 truncate ${tx}`}>{c.country}</span>
                <span className={`text-[10px] ${th}`}>{c.users} usr · {fmtDur(c.seconds)}</span>
                <span className="text-[10px] font-bold text-[#E9641A] tabular-nums w-8 text-right">{c.sessions}</span>
              </div>
            ))}
          </div>
          {data.cities.length > 0 && (
            <>
              <h4 className={`text-xs font-bold mt-4 mb-2 ${tx}`}>Top villes</h4>
              <div className="space-y-1.5">
                {data.cities.slice(0, 8).map(c => (
                  <div key={c.city} className="flex items-center justify-between">
                    <span className={`text-[11px] truncate ${light ? 'text-gray-600' : 'text-white/60'}`}>{c.city}</span>
                    <span className="text-[10px] font-bold text-[#E9641A]">{c.sessions}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Devices + plans */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className={`rounded-xl border p-4 ${card}`} data-testid="devices-chart">
          <h3 className={`text-sm font-bold mb-1 ${tx}`}>Appareils utilisés</h3>
          {data.device_types.length === 0 ? <p className={`text-xs py-8 text-center ${th}`}>Aucune donnée pour le moment</p> : (
            <>
              <div className="h-[210px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.device_types} dataKey="sessions" nameKey="type" innerRadius={48} outerRadius={78} paddingAngle={3}>
                      {data.device_types.map((d, i) => <Cell key={d.type} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <ReTooltip formatter={(v, n) => [`${v} lecture(s)`, n]} contentStyle={{ background: light ? '#fff' : '#1a0e2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }} />
                    <Legend formatter={(v) => <span style={{ fontSize: 11, color: light ? '#334155' : 'rgba(255,255,255,0.7)' }}>{v}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1 mt-1">
                {data.devices_detail.map(d => (
                  <div key={d.device} className="flex items-center justify-between">
                    <span className={`text-[11px] truncate ${light ? 'text-gray-600' : 'text-white/60'}`}>{d.device}</span>
                    <span className={`text-[10px] ${th}`}>{d.sessions} lect. · {fmtDur(d.seconds)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className={`rounded-xl border p-4 ${card}`} data-testid="plans-chart">
          <h3 className={`text-sm font-bold mb-3 ${tx}`}>Répartition par offre (abonnés actifs)</h3>
          {data.plans.length === 0 && <p className={`text-xs ${th}`}>Aucune donnée</p>}
          <div className="space-y-3">
            {data.plans.map((p, i) => {
              const maxA = Math.max(1, ...data.plans.map(x => x.active));
              return (
                <div key={p.plan}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold ${tx}`}>{p.plan}</span>
                    <span className={`text-[10px] ${th}`}>{p.active} actif(s) · {p.total} au total · revenu {money(p.revenue)}</span>
                  </div>
                  <div className={`h-2 rounded-full overflow-hidden ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
                    <div className="h-full rounded-full" style={{ width: `${(p.active / maxA) * 100}%`, background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  </div>
                </div>
              );
            })}
          </div>
          <h3 className={`text-sm font-bold mt-5 mb-2 ${tx}`}>Utilisateurs les plus actifs</h3>
          {data.top_users.length === 0 ? <p className={`text-xs ${th}`}>Aucune donnée de visionnage pour le moment</p> : (
            <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1" data-testid="top-users-list">
              {data.top_users.map((u, i) => (
                <div key={u.user_id} className="flex items-center gap-2">
                  <span className={`text-[10px] w-4 font-bold ${th}`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold truncate ${tx}`}>{u.name || u.email || u.user_id}</p>
                    {u.email && u.name && <p className={`text-[10px] truncate ${th}`}>{u.email}</p>}
                  </div>
                  <span className={`text-[10px] ${th}`}>{u.contents_count} contenu(s) · {u.sessions} lect.</span>
                  <span className="text-[11px] font-bold text-[#E9641A] whitespace-nowrap">{fmtDur(u.seconds)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Contents ranking */}
      <div className={`rounded-xl border p-4 ${card}`} data-testid="contents-stats">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className={`text-sm font-bold ${tx}`}>Contenus : temps de visionnage & nombre de lectures</h3>
          <div className="flex items-center gap-2">
            <select value={contentMetric} onChange={e => setContentMetric(e.target.value)}
              className={`text-xs rounded-lg px-2 py-1.5 border focus:outline-none ${light ? 'bg-white border-gray-200 text-gray-700' : 'bg-white/5 border-white/10 text-white'}`}
              data-testid="content-metric-select">
              <option value="seconds">Par temps visionné</option>
              <option value="views">Par nombre de lectures</option>
              <option value="unique_viewers">Par spectateurs uniques</option>
            </select>
            <button onClick={() => setContentOrder('top')} className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${contentOrder === 'top' ? 'bg-[#E9641A] text-white' : light ? 'bg-gray-100 text-gray-600' : 'bg-white/5 text-white/50'}`} data-testid="content-order-top"><TrendingUp size={12} /> Plus regardés</button>
            <button onClick={() => setContentOrder('flop')} className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${contentOrder === 'flop' ? 'bg-[#E9641A] text-white' : light ? 'bg-gray-100 text-gray-600' : 'bg-white/5 text-white/50'}`} data-testid="content-order-flop"><TrendingDown size={12} /> Moins regardés</button>
            <div className="relative">
              <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${th}`} />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…"
                className={`pl-8 pr-2 py-1.5 rounded-lg text-xs w-44 border focus:outline-none focus:border-[#E9641A] ${light ? 'bg-white border-gray-200 text-gray-800' : 'bg-white/5 border-white/10 text-white'}`}
                data-testid="content-stats-search" />
            </div>
          </div>
        </div>
        <table className="w-full text-left">
          <thead>
            <tr className={light ? 'border-b border-gray-200' : 'border-b border-white/10'}>
              {['#', 'Contenu', 'Type', 'Lectures', 'Temps visionné', 'Spectateurs uniques'].map(h => (
                <th key={h} className={`px-2 py-2 text-[10px] font-semibold uppercase tracking-wide ${th}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className={light ? 'divide-y divide-gray-100' : 'divide-y divide-white/5'}>
            {contents.map((c, i) => (
              <tr key={c.content_id} data-testid={`content-stat-row-${i}`}>
                <td className={`px-2 py-2 text-xs font-bold ${th}`}>{i + 1}</td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-2">
                    {c.poster_url && <img src={c.poster_url} alt="" className="w-7 h-10 object-cover rounded flex-none" />}
                    <span className={`text-xs font-semibold ${tx}`}>{c.title}</span>
                  </div>
                </td>
                <td className={`px-2 py-2 text-[11px] capitalize ${light ? 'text-gray-600' : 'text-white/60'}`}>{c.type}</td>
                <td className={`px-2 py-2 text-xs tabular-nums font-bold ${c.views ? 'text-[#E9641A]' : th}`}>{c.views}</td>
                <td className={`px-2 py-2 text-xs tabular-nums ${tx}`}>{fmtDur(c.seconds)}</td>
                <td className={`px-2 py-2 text-xs tabular-nums ${light ? 'text-gray-600' : 'text-white/60'}`}>{c.unique_viewers}</td>
              </tr>
            ))}
            {contents.length === 0 && <tr><td colSpan={6} className={`px-2 py-8 text-center text-xs ${th}`}>Aucun résultat</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
